import React, { memo, useCallback, useContext, useMemo } from 'react';
import DetailsCard from './DetailsCard';
import S from './style/DeliveryMethodCard';
import useI18n from '../../../../i18n/hooks/useI18n';
import TransactionContext from '../contexts/TransactionContext';
import TabsContext from '../contexts/TabsContext';
import { TAB_TYPE_BUYING } from '../../constants';

const DeliveryMethodCard: React.FC<{
  children: React.ReactNode;
}> = function DeliveryMethodCard({ children }) {
  const transaction = useContext(TransactionContext);
  const tabType = useContext(TabsContext);
  const servicePoint =
    tabType === TAB_TYPE_BUYING ? transaction?.destinationPoint : transaction?.packingPoint;

  const operator = servicePoint?.id?.split(':')[0];
  const isInPost = servicePoint?.brand === 'inpost';
  const servicePointId = servicePoint?.id?.split(':')[1];
  const isLocker = isInPost && servicePointId?.indexOf('POP-') === -1;
  const inPostShipmentKey = isLocker ? 'inpost' : 'inpost_pop';

  const i18n = useI18n();
  const i18nKey = useCallback(
    (key: string | TemplateStringsArray) => i18n(`dlv_orders_details_${key}`),
    [i18n],
  );

  const i18nSubtitleKey = useCallback(
    (key: string | TemplateStringsArray) => i18n(`dlv_order_card_desktop_${key}`),
    [i18n],
  );
  const subtitle = useMemo(() => {
    if (transaction?.deliveryType === 'D2D') {
      return i18nKey`door_to_door`;
    }

    return tabType === TAB_TYPE_BUYING ? i18nSubtitleKey`pickup` : i18nSubtitleKey`post`;
  }, [transaction, tabType]);

  const i18nShipmentKey = useCallback(
    (key: string | TemplateStringsArray) => i18n(`dlv_shipment_operator_${key}`),
    [i18n],
  );

  const title = isInPost
    ? `${i18nShipmentKey(inPostShipmentKey)} ${servicePointId}`
    : `${i18nShipmentKey(operator)} ${servicePointId}`;

  const setvicePointInfo = useMemo(
    () => (
      <S.Wrapper>
        <S.Title>{servicePoint && title}</S.Title>
        <S.Description loadingWidth="30%">{servicePoint && servicePoint?.street}</S.Description>
        <S.Description loadingWidth="40%">
          {servicePoint && `${servicePoint?.postalCode} ${servicePoint?.city}`}
        </S.Description>
        {servicePoint?.description && (
          <S.Description loadingWidth="40%">{`[${servicePoint?.description}]`}</S.Description>
        )}
      </S.Wrapper>
    ),
    [servicePoint, title],
  );

  const doorToDoorInfo = useMemo(() => {
    if (tabType === TAB_TYPE_BUYING) {
      return (
        <S.Wrapper>
          <S.TitleWithMargin>{transaction?.destinationAddress?.brand}</S.TitleWithMargin>
          <S.TitleWithMargin>{i18nKey`deliveryaddress`}</S.TitleWithMargin>
          <S.Description loadingWidth="30%">
            {transaction?.destinationAddress?.streetName}{' '}
            {transaction?.destinationAddress?.streetNumber}
          </S.Description>
          <S.Description loadingWidth="30%">
            {transaction?.destinationAddress?.postCode} {transaction?.destinationAddress?.city}
          </S.Description>
        </S.Wrapper>
      );
    }

    const [year, month, day] = (transaction?.pickUpWindow?.date || '').split('-');
    return (
      <S.Wrapper>
        <S.TitleWithMargin>{transaction?.packingAddress?.brand}</S.TitleWithMargin>
        <S.TitleWithMargin>{i18nKey`pickupaddress`}</S.TitleWithMargin>
        <S.Description loadingWidth="30%">
          {transaction?.packingAddress?.streetName} {transaction?.packingAddress?.streetNumber}
        </S.Description>
        <S.Description loadingWidth="30%">
          {transaction?.packingAddress?.postCode} {transaction?.packingAddress?.city}
        </S.Description>
        <S.TitleWithMargin>{i18nKey`pickupdate`}</S.TitleWithMargin>
        <S.Description loadingWidth="30%">
          {day}.{month}.{year}, {transaction?.pickUpWindow?.timeFrom} -{' '}
          {transaction?.pickUpWindow?.timeTo}
        </S.Description>
      </S.Wrapper>
    );
  }, []);

  return (
    <DetailsCard data-cy="delivery-method-info" title={i18nKey`deliverymethod`} subtitle={subtitle}>
      {transaction?.deliveryType === 'D2D' ? doorToDoorInfo : setvicePointInfo}
      {children}
    </DetailsCard>
  );
};

export default memo(DeliveryMethodCard);
